import { FC, useCallback } from 'react';
import { useFragment, useMutation } from 'react-relay';
import { Formik } from 'formik';
import graphql from 'babel-plugin-relay/macro';

import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import FormControlLabel from '@mui/material/FormControlLabel';
import Form from 'components/Form';
import TextField from 'components/TextField';
import TwoColumn from 'components/TwoColumn';
import Checkbox from 'components/Checkbox';

import { stringToNumber, textFieldValue, useErrorHandler } from 'lib';
import { EtgSettings_settings$key } from '__generated__/EtgSettings_settings.graphql';
import { EtgSettingsMutation } from '__generated__/EtgSettingsMutation.graphql';
import InputAdornment from '@mui/material/InputAdornment';

type FormFields = {
  keyId: string;
  key: string;
  incrementalImport: boolean;
  incrementalBatchSize: string;
  fullLogContext: boolean;
  rateUpdatesEnabled: boolean;
  maxRateUpdateFrequency: string;
};

type Props = {
  settings: EtgSettings_settings$key;
};

const EtgSettings: FC<Props> = props => {
  const errorHandler = useErrorHandler();

  const { etg } = useFragment(
    graphql`
      fragment EtgSettings_settings on Settings {
        etg {
          keyId
          key
          incrementalImport
          incrementalBatchSize
          fullLogContext
          rateUpdatesEnabled
          maxRateUpdateFrequency
        }
      }
    `,
    props.settings
  );

  const [update, updating] = useMutation<EtgSettingsMutation>(graphql`
    mutation EtgSettingsMutation($input: UpdateSettingsInput!) {
      updateSettings(input: $input) {
        ...EtgSettings_settings
      }
    }
  `);

  const initial = {
    keyId: etg?.keyId ?? '',
    key: etg?.key ?? '',
    incrementalImport: etg?.incrementalImport ?? false,
    incrementalBatchSize: textFieldValue(etg?.incrementalBatchSize),
    fullLogContext: etg?.fullLogContext ?? false,
    rateUpdatesEnabled: etg?.rateUpdatesEnabled ?? false,
    maxRateUpdateFrequency: textFieldValue(etg?.maxRateUpdateFrequency),
  };

  const handleSubmit = useCallback(
    (values: FormFields) => {
      update({
        variables: {
          input: {
            etg: {
              keyId: values.keyId,
              key: values.key,
              incrementalImport: values.incrementalImport,
              incrementalBatchSize: stringToNumber(values.incrementalBatchSize),
              fullLogContext: values.fullLogContext,
              rateUpdatesEnabled: values.rateUpdatesEnabled,
              maxRateUpdateFrequency: stringToNumber(
                values.maxRateUpdateFrequency
              ),
            },
          },
        },

        ...errorHandler({
          successMessage: 'Išsaugota',
        }),
      });
    },
    [errorHandler, update]
  );

  return (
    <Formik<FormFields> initialValues={initial} onSubmit={handleSubmit}>
      <Form loading={updating}>
        <Paper sx={{ p: 2 }}>
          <Stack spacing={4}>
            <TextField fullWidth name="keyId" label="Pixel ID" />
            <TextField fullWidth name="key" label="Access Token" />

            <TwoColumn
              left={
                <FormControlLabel
                  control={<Checkbox name="incrementalImport" />}
                  label="Kasdienis importas"
                />
              }
              right={
                <FormControlLabel
                  control={<Checkbox name="fullLogContext" />}
                  label="Saugoti pilną importo informaciją istorijoje"
                />
              }
            />

            <TextField
              fullWidth
              name="incrementalBatchSize"
              label="Kasdienio importo partijos dydis"
            />

            <TwoColumn
              left={
                <FormControlLabel
                  control={<Checkbox name="rateUpdatesEnabled" />}
                  label="Automatiniai kambario kainos atnaujinimai"
                />
              }
              right={
                <TextField
                  fullWidth
                  name="maxRateUpdateFrequency"
                  label="Maksimalus kambario kainų atnaujinimo dažnumas"
                  InputProps={{
                    endAdornment: <InputAdornment position="end" />,
                  }}
                />
              }
            />
          </Stack>
        </Paper>
      </Form>
    </Formik>
  );
};

export default EtgSettings;
